import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { languageNativeName } from '../../../utils/localization/languages';
import { SOURCE_LOCALE } from '../../../utils/localization/apply';
import { useUiString } from '../../../utils/hook/useUiString';
import styles from './LanguageSwitcher.module.scss';

interface Props {
    locale: string;
    locales: string[];
    onChange: (locale: string) => void;
}

export const LanguageSwitcher: FC<Props> = ({ locale, locales, onChange }) => {
    const t = useUiString();
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

    const options = [SOURCE_LOCALE, ...locales.filter((code) => code !== SOURCE_LOCALE)];

    useLayoutEffect(() => {
        if (!open || !rootRef.current) return;
        const rect = rootRef.current.getBoundingClientRect();
        setMenuPos({
            top: rect.bottom + 8,
            right: Math.max(8, window.innerWidth - rect.right),
        });
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const onPointer = (event: MouseEvent) => {
            const target = event.target as Node;
            if (rootRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        const onScroll = () => setOpen(false);

        document.addEventListener('mousedown', onPointer);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onScroll);
        return () => {
            document.removeEventListener('mousedown', onPointer);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onScroll);
        };
    }, [open]);

    return (
        <div className={styles.root} ref={rootRef}>
            <button
                type="button"
                className={styles.trigger}
                aria-label={t('language')}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
            >
                <span className={styles.triggerLabel}>{languageNativeName(locale)}</span>
                <span className={`${styles.caret} ${open ? styles.caretOpen : ''}`} />
            </button>
            {open &&
                createPortal(
                    <ul
                        ref={menuRef}
                        className={styles.menu}
                        role="listbox"
                        style={{ top: menuPos.top, right: menuPos.right }}
                    >
                        {options.map((code) => {
                            const active = code === locale;
                            return (
                                <li key={code} role="presentation">
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={active}
                                        className={active ? styles.optionActive : styles.option}
                                        onClick={() => {
                                            onChange(code);
                                            setOpen(false);
                                        }}
                                    >
                                        {languageNativeName(code)}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>,
                    document.body
                )}
        </div>
    );
};
